import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, takeUntil } from 'rxjs/operators';
import { UserService } from './api/api/user.service';
import { PersonalizationsService } from './base/services/personalizations/personalizations.service';
import { ColorsService } from './core/colors/colors.service';
import { ProfileChangeObject } from './core/profile/models/profile-change-event';
import { ProfileService } from './core/profile/profile.service';
import { TranslateService } from './core/translation/translate.service';
import { LoadingProComponent } from './finish-pro/components/loading-pro/loading-pro.component';
import { Environment } from './models/environment';
import { LoadingService } from './shared/loading/loading.service';
import { MessageInterface, MessageService } from './shared/message/message.service';
import { IvMessageComponent } from './shared/components/iv-message/iv-message.component';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * Loading component from finish-pro.
   */
  @ViewChild('finishProLoading') finishProLoading: LoadingProComponent;

  /**
   * IvLoading component from platform.
   */
  @ViewChild('messenger') messenger: IvMessageComponent;

  /**
   * Sidenav component from Angular Material.
   */
  @ViewChild('sidenavCont') sidenavCont: MatSidenavContainer;

  /**
   * Boolean that controls if user is logged.
   */
  isLogged = false;

  /**
   * Boolean that hides loader on finish pro flow
   */
  isFinishProActive: boolean;

  /**
   * Boolean that controls if user is logged.
   */
  isLoggedOut = true;

  /**
   * Boolean that shows /hides the spinner view.
   */
  loading = false;

  /**
   * String with the actual language.
   */
  language: string;

  /**
   * Boolean used for the conditional of the language selector according the configuration of the customization.
   */
  hideLangSelect: boolean;

  /**
   * Boolean used for the conditional of the language selector according to the configuration of the forced language.
   */
  forcedLanguage: boolean;

  /**
   * Boolean show/hide the language selector according the configuration of the customization.
   */
  showLanguageSelector: boolean;

  /**
   * Boolean show/hide the custom footer with custom links
   */
  customFooter: boolean = false;

  /**
   * String that contains the actual URL of the route
   */
  url: string = '';

  /**
   * Boolean that validates if show/hide the actual button to redirect
   */
  showRedirectButton: boolean = false;

  environment: Environment;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _translate: TranslateService,
    private _user: UserService,
    private _profile: ProfileService,
    private _loading: LoadingService,
    private _message: MessageService,
    private _colors: ColorsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private ref: ChangeDetectorRef,
    private _personalization: PersonalizationsService
  ) {
    this.environment = this._personalization.sendEnv;
    this.initApp();
    (<any>window).pdfWorkerSrc = '/pdfjs-lib/pdf.worker.min.js';
  }

  ngAfterContentChecked(): void {
    this.ref.detectChanges();
  }

  ngOnInit(): void {
    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
    this.customFooter = this.environment?.features.footerLinks?.active ? true : false;
    if (!this.isLogged) {
      this.url = window.location.href;
      if (this.url.includes('finishOperation')) {
        this.showRedirectButton = true;
      } else {
        this.showRedirectButton = false;
      }
    } else {
      this.showRedirectButton = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.unsubscribe();
  }

  private getLanguage(): string {
    const param = document.location.search;
    let active = this.environment.lang?.active ?? ['es'];
    const forced = this.environment.lang?.force ?? null;

    this._translate.activeLanguages = active;

    if (active.length === 1) {
      return active[0];
    }

    const cookieLang = this.getLangFromCookie();
    if (cookieLang) {
      return cookieLang;
    }

    if (forced) {
      return forced;
    }

    const urlLang = this.getLangFromUrl(param, active);
    if (urlLang) {
      return urlLang;
    }

    const browserLang = this.getLangFromBrowser(active);
    if (browserLang) {
      return browserLang;
    }
    return this.environment.lang?.default ?? 'es';
  }

  private getLangFromCookie(): string | null {
    return document.cookie.replace(/(?:(?:^|.*;\s*)lang\s*=\s*([^;]*).*$)|^.*$/, '$1') || null;
  }

  private getLangFromUrl(param: string, active: string[]): string | null {
    const regex = /[?&]lang=([a-z]{2})/i;
    const match = regex.exec(param);
    if (match) {
      const lang = match[1];
      return active.includes(lang) ? lang : null;
    }
    return null;
  }

  private getLangFromBrowser(active: string[]): string | null {
    const idiomas = window.navigator.languages;

    if (Array.isArray(idiomas) && idiomas.length) {
      for (const idioma of idiomas) {
        const lang = idioma.split('-')[0];
        if (active.includes(lang)) {
          return lang;
        }
      }
    }

    const lang = window.navigator.language.split('-')[0];
    return active.includes(lang) ? lang : null;
  }

  /**
   * Reassigns an object's value to a variable
   */
  initApp(): void {
    this.hideLangSelect = this.environment.lang.hideSelect ? this.environment.lang.hideSelect : false;
    this.forcedLanguage = this.environment.lang.force ? true : false;
    this.showLanguageSelector =
      this.environment.lang && this.environment.lang.active.length > 1 && !this.hideLangSelect && !this.forcedLanguage;
    this.isFinishProActive = this._personalization.hasFinishProActive;
    if (localStorage.getItem('lang') && this.environment.lang && !this.environment.lang.force) {
      this.language = localStorage.getItem('lang');
    } else {
      this.language = this.getLanguage() || 'es';
    }

    const sessionUser = this._profile.getToken();

    this._translate.set(this.language, true);

    if (sessionUser) {
      this._user
        .userChangeActiveLanguage(this.language, sessionUser)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            this._loading.set(false);
          },
          (error) => {
            this._loading.set(false);
            this._message.showError(error);
          }
        );
    }

    this._profile.onProfileChange.subscribe((result: ProfileChangeObject) => {
      this.isLogged = result.status;
      if (result.logout) {
        this._colors.deleteColors();
        this.router.navigate(['/login']);
      }
    });
    this._profile.init();

    if (this.environment.class) {
      console.log(`Personalization theme: ${this.environment.class}-theme`);
      document.body.classList.add(this.environment.class + '-theme');
    }
    this._loading.onChange.pipe(takeUntil(this.destroy$)).subscribe((result: boolean) => {
      this.loading = result;
    });

    this._message.onNew.pipe(takeUntil(this.destroy$)).subscribe((result: MessageInterface) => {
      this.messenger.show(result);
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;

          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data.title) {
              return child.snapshot.data.title;
            } else {
              return null;
            }
          }

          return null;
        })
      )
      .subscribe((data: string) => {
        if (data) {
          if (data.length === 2) {
            const section: string = this._translate.instant('title_' + data[0]);
            const mainSection: string = this._translate.instant('title_' + data[1]);
            const fullSection: string = section.concat(' - ' + mainSection).concat(' - IvCert');
            this.titleService.setTitle(fullSection);
          } else {
            const section: string = this._translate.instant('title_' + data) + ' - IvCert';
            this.titleService.setTitle(section);
          }
        }
      });
  }
}
